import { useEffect, useState } from "react";
import API from "@/lib/api";
import { CreditCard, CalendarClock, PauseCircle, PlayCircle, XCircle, RefreshCcw, Loader2, Heart, ArrowLeftRight, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ChangePlanModal from "@/components/ChangePlanModal";
import PatientHelpTooltip from "@/components/PatientHelpTooltip";

/**
 * Patient self-service subscription management card.
 *
 * Shows each active/canceling/paused Stripe subscription with:
 *  - Product name, $/month (or $/3-months), next renewal date
 *  - Status pill (Active / Canceling at period end / Paused until <date>)
 *  - Pause for 30 days   ─┐
 *  - Cancel at period end ┼── confirm-then-act, no surprises
 *  - Resume / Reactivate ─┘
 *
 * Self-hides if the patient has zero subscriptions on record. Live state is
 * pulled from Stripe via /api/subscriptions/me on mount and after every action,
 * so the card never lies about reality.
 */
export default function SubscriptionsCard() {
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [busyId, setBusyId] = useState(null);
  // Multi-step confirmation:
  //   { subId, step: "retain"|"confirm-cancel"|"confirm-pause", sub }
  const [confirm, setConfirm] = useState(null);
  const [changePlan, setChangePlan] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await API.post("/payments/portal", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
      else toast.error("Couldn't open the payment portal — please try again.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open the payment portal.");
    } finally {
      setOpeningPortal(false);
    }
  };

  const refresh = async () => {
    try {
      const { data } = await API.get("/subscriptions/me");
      setSubs(data.subscriptions || []);
    } catch (e) {
      // Silent — card just won't render.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
  };

  const intervalLabel = (sub) => {
    if (sub.interval_count > 1) return `every ${sub.interval_count} ${sub.interval}s`;
    return `per ${sub.interval}`;
  };

  const statusPill = (sub) => {
    if (sub.status === "canceled") return { label: "Canceled", cls: "bg-slate-200 text-slate-600" };
    if (sub.cancel_at_period_end) return { label: "Cancels at period end", cls: "bg-amber-100 text-amber-800" };
    if (sub.paused) return { label: `Paused until ${formatDate(sub.pause_resumes_at)}`, cls: "bg-blue-100 text-blue-800" };
    if (sub.status === "past_due") return { label: "Payment past due", cls: "bg-red-100 text-red-800" };
    if (sub.status === "active" || sub.status === "trialing") return { label: "Active", cls: "bg-emerald-100 text-emerald-800" };
    return { label: sub.status || "—", cls: "bg-slate-100 text-slate-700" };
  };

  const runAction = async (subId, action, successMsg, source, body = null) => {
    setBusyId(subId);
    try {
      const qs = source ? `?source=${encodeURIComponent(source)}` : "";
      await API.post(`/subscriptions/${subId}/${action}${qs}`, body || {});
      toast.success(successMsg);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update subscription");
    } finally {
      setBusyId(null);
      setConfirm(null);
      setPauseChoice({ days: 30, customDate: "" });
    }
  };

  // Pause duration picker state — used inside both retain & confirm-pause dialogs.
  const [pauseChoice, setPauseChoice] = useState({ days: 30, customDate: "" });

  const buildPauseBody = () => {
    if (pauseChoice.customDate) {
      const d = new Date(pauseChoice.customDate);
      if (Number.isNaN(d.getTime())) return null;
      return { resumes_at: d.toISOString() };
    }
    return { days: pauseChoice.days };
  };

  const pauseLabel = () => {
    if (pauseChoice.customDate) return `until ${pauseChoice.customDate}`;
    return `${pauseChoice.days} days`;
  };

  const maxCustomDate = (() => {
    const d = new Date(); d.setDate(d.getDate() + 180);
    return d.toISOString().slice(0, 10);
  })();

  const minCustomDate = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();

  if (loading) return null; // first render shouldn't flash an empty card
  if (!subs.length) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="subscriptions-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Your subscriptions
              <PatientHelpTooltip
                testId="subscriptions"
                title="How do subscriptions work?"
                body={
                  <>
                    <p>Your medication is delivered on a regular schedule (monthly or every 3 months for Royal) so you never run out mid-titration.</p>
                    <p><b>Pause for 30 days</b> — skip the next shipment, then auto-resume. Use if you&apos;re traveling or have leftover supply.</p>
                    <p><b>Cancel</b> — stops future renewals. You keep access through the end of your current billing period.</p>
                    <p><b>Change plan</b> — switch tiers (e.g. Monthly → Royal). The change applies at your next renewal so you never lose paid time.</p>
                    <p className="text-[11px] opacity-70">All changes are reversible until the renewal date — just tap the same button again to undo.</p>
                  </>
                }
              />
            </h3>
            <p className="text-xs text-slate-500">Pause or cancel anytime — changes take effect at the end of your current billing period, so you never lose access early.</p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={openPortal}
          disabled={openingPortal}
          className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 shrink-0"
          data-testid="manage-payment-method-btn"
        >
          {openingPortal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Wallet className="w-3.5 h-3.5 mr-1.5" /> Manage payment method</>}
        </Button>
      </div>

      <div className="space-y-3">
        {subs.map((sub) => {
          const pill = statusPill(sub);
          const isBusy = busyId === sub.id;
          return (
            <div key={sub.id} className="rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`subscription-row-${sub.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900 truncate">{sub.product_name}</p>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${pill.cls}`} data-testid={`subscription-status-${sub.id}`}>
                    {pill.label}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="font-medium text-slate-700">${Number(sub.amount).toFixed(2)} {intervalLabel(sub)}</span>
                  <span className="flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" />
                    {sub.cancel_at_period_end ? `Ends ${formatDate(sub.next_renewal_at)}` : `Next renewal ${formatDate(sub.next_renewal_at)}`}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {/* Change plan (upgrade / downgrade) */}
                {sub.status !== "canceled" && !sub.cancel_at_period_end && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setChangePlan(sub)}
                    disabled={isBusy}
                    className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50"
                    data-testid={`subscription-change-plan-${sub.id}`}
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Change plan
                  </Button>
                )}

                {/* Resume scheduled cancellation */}
                {sub.cancel_at_period_end && sub.status !== "canceled" && (
                  <Button
                    size="sm"
                    onClick={() => runAction(sub.id, "resume", "Subscription reactivated.")}
                    disabled={isBusy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                    data-testid={`subscription-resume-${sub.id}`}
                  >
                    {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCcw className="w-3.5 h-3.5 mr-1" /> Keep it active</>}
                  </Button>
                )}

                {/* Unpause */}
                {sub.paused && (
                  <Button
                    size="sm"
                    onClick={() => runAction(sub.id, "unpause", "Subscription resumed.")}
                    disabled={isBusy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                    data-testid={`subscription-unpause-${sub.id}`}
                  >
                    {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><PlayCircle className="w-3.5 h-3.5 mr-1" /> Resume now</>}
                  </Button>
                )}

                {/* Pause */}
                {!sub.paused && !sub.cancel_at_period_end && sub.status !== "canceled" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setPauseChoice({ days: 30, customDate: "" }); setConfirm({ subId: sub.id, sub, step: "confirm-pause" }); }}
                    disabled={isBusy}
                    className="rounded-full border-blue-200 text-blue-700 hover:bg-blue-50"
                    data-testid={`subscription-pause-${sub.id}`}
                  >
                    <PauseCircle className="w-3.5 h-3.5 mr-1" /> Pause
                  </Button>
                )}

                {/* Cancel at period end — surfaces a "pause instead?" retention prompt first */}
                {!sub.cancel_at_period_end && sub.status !== "canceled" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirm({ subId: sub.id, sub, step: "retain" })}
                    disabled={isBusy}
                    className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`subscription-cancel-${sub.id}`}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm dialog
          step="retain"           → "Wait — pause instead?" retention prompt (default after Cancel click)
          step="confirm-cancel"   → final cancel confirmation if patient still wants to cancel
          step="confirm-pause"    → simple confirmation when patient clicked Pause directly */}
      <Dialog open={!!confirm} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="subscription-confirm-dialog">
          {confirm?.step === "retain" && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                    <Heart className="w-4 h-4 text-blue-600" />
                  </div>
                  <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Wait — pause instead?</DialogTitle>
                </div>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Life happens. If you&apos;re traveling, between paychecks, or just need a break — pausing is usually a better fit than canceling.</p>
                <ul className="text-xs text-slate-600 space-y-1.5 bg-slate-50 rounded-xl p-3">
                  <li className="flex gap-2"><span className="text-blue-600 font-bold">✓</span> No charge during the pause</li>
                  <li className="flex gap-2"><span className="text-blue-600 font-bold">✓</span> Auto-resumes after — or resume early anytime</li>
                  <li className="flex gap-2"><span className="text-blue-600 font-bold">✓</span> Your prescription stays on file</li>
                  <li className="flex gap-2"><span className="text-blue-600 font-bold">✓</span> No re-evaluation needed when you return</li>
                </ul>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3" data-testid="pause-duration-picker">
                  <p className="text-xs font-semibold text-blue-900 mb-2">How long?</p>
                  <div className="grid grid-cols-3 gap-1.5 mb-2">
                    {[30, 60, 90].map((d) => (
                      <button
                        key={d} type="button"
                        onClick={() => setPauseChoice({ days: d, customDate: "" })}
                        className={`text-xs rounded-lg py-1.5 border transition-colors ${pauseChoice.days === d && !pauseChoice.customDate ? "bg-blue-600 text-white border-blue-600" : "bg-white border-blue-200 text-blue-700 hover:bg-blue-50"}`}
                        data-testid={`pause-days-${d}`}
                      >
                        {d} days
                      </button>
                    ))}
                  </div>
                  <input
                    type="date"
                    min={minCustomDate} max={maxCustomDate}
                    value={pauseChoice.customDate}
                    onChange={(e) => setPauseChoice({ days: 30, customDate: e.target.value })}
                    className="w-full text-xs rounded-lg border border-blue-200 bg-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    data-testid="pause-custom-date"
                  />
                  <p className="text-[10px] text-blue-600 mt-1">Or pick any date up to 180 days from today.</p>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">If you cancel, you&apos;ll keep access until <b>{formatDate(confirm.sub?.next_renewal_at)}</b>. After that you&apos;ll need a new prescription review to restart.</p>
              </div>
              <DialogFooter className="!flex-col-reverse sm:!flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirm({ ...confirm, step: "confirm-cancel" })}
                  disabled={busyId === confirm?.subId}
                  className="rounded-full border-red-200 text-red-700 hover:bg-red-50 w-full sm:w-auto"
                  data-testid="subscription-retain-cancel-anyway"
                >
                  No thanks, cancel anyway
                </Button>
                <Button
                  onClick={() => {
                    const body = buildPauseBody();
                    if (!body) { toast.error("Pick a valid date"); return; }
                    runAction(confirm.subId, "pause", `Subscription paused ${pauseLabel()}. We'll see you back soon!`, "retention_prompt", body);
                  }}
                  disabled={busyId === confirm?.subId}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-full sm:w-auto"
                  data-testid="subscription-retain-accept-pause"
                >
                  {busyId === confirm?.subId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><PauseCircle className="w-3.5 h-3.5 mr-1.5" /> Pause {pauseLabel()}</>}
                </Button>
              </DialogFooter>
            </>
          )}

          {confirm?.step === "confirm-cancel" && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Cancel subscription?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600">You&apos;ll keep access until <b>{formatDate(confirm.sub?.next_renewal_at)}</b> and won&apos;t be charged again. You can re-subscribe anytime — your prescription will need to be renewed by your provider.</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirm(null)}>Never mind</Button>
                <Button
                  onClick={() => runAction(confirm.subId, "cancel", "We'll cancel at the end of your billing period.", "retention_prompt_declined")}
                  disabled={busyId === confirm?.subId}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="subscription-confirm-submit"
                >
                  {busyId === confirm?.subId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Yes, cancel"}
                </Button>
              </DialogFooter>
            </>
          )}

          {confirm?.step === "confirm-pause" && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Pause your subscription</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-slate-600">We&apos;ll skip your billing during the pause. You can resume anytime — otherwise it auto-resumes on the date you pick.</p>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3" data-testid="pause-duration-picker-direct">
                  <p className="text-xs font-semibold text-blue-900 mb-2">How long?</p>
                  <div className="grid grid-cols-3 gap-1.5 mb-2">
                    {[30, 60, 90].map((d) => (
                      <button
                        key={d} type="button"
                        onClick={() => setPauseChoice({ days: d, customDate: "" })}
                        className={`text-xs rounded-lg py-1.5 border transition-colors ${pauseChoice.days === d && !pauseChoice.customDate ? "bg-blue-600 text-white border-blue-600" : "bg-white border-blue-200 text-blue-700 hover:bg-blue-50"}`}
                        data-testid={`pause-direct-days-${d}`}
                      >
                        {d} days
                      </button>
                    ))}
                  </div>
                  <input
                    type="date"
                    min={minCustomDate} max={maxCustomDate}
                    value={pauseChoice.customDate}
                    onChange={(e) => setPauseChoice({ days: 30, customDate: e.target.value })}
                    className="w-full text-xs rounded-lg border border-blue-200 bg-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    data-testid="pause-direct-custom-date"
                  />
                  <p className="text-[10px] text-blue-600 mt-1">Or pick any date up to 180 days from today.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirm(null)}>Never mind</Button>
                <Button
                  onClick={() => {
                    const body = buildPauseBody();
                    if (!body) { toast.error("Pick a valid date"); return; }
                    runAction(confirm.subId, "pause", `Subscription paused ${pauseLabel()}.`, null, body);
                  }}
                  disabled={busyId === confirm?.subId}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="subscription-confirm-pause-submit"
                >
                  {busyId === confirm?.subId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Pause ${pauseLabel()}`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Plan upgrade / downgrade modal */}
      <ChangePlanModal
        open={!!changePlan}
        sub={changePlan}
        onClose={() => setChangePlan(null)}
        onChanged={() => refresh()}
      />
    </div>
  );
}
